import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import cn from 'classnames';
import axios from 'axios';
import styles from './hosted-by.scss';

/**
 * Generate initials from a name
 */
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

/**
 * Avatar component with initials fallback
 */
function Avatar({ name, avatarUrl, size = 40, removeHostsAvatar = false }) {
  if (removeHostsAvatar) return <span />;
  const [imageError, setImageError] = useState(false);

  if (avatarUrl && !imageError) {
    return (
      <img src={avatarUrl} alt={name} className={styles.avatarImage} style={{ width: size, height: size }} onError={() => setImageError(true)} />
    );
  }

  return (
    <div className={styles.avatarInitials} style={{ width: size, height: size, fontSize: size * 0.35 }}>
      {getInitials(name)}
    </div>
  );
}

Avatar.propTypes = {
  name: PropTypes.string.isRequired,
  avatarUrl: PropTypes.string,
  size: PropTypes.number,
};

/**
 * Hosts Modal Component
 */
function HostsModal({ event, settings, onClose }) {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHosts = async () => {
      try {
        setLoading(true);
        const eventId = event.get('id');
        const baseUrl = process.env.EVENTVIEWER_SERVER || '';
        const response = await axios.get(`${baseUrl}/api/v1/event-page/hosts/events/${eventId}`);
        setHosts(response.data.hosts || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching hosts:', err);
        setError('Failed to load hosts');
      } finally {
        setLoading(false);
      }
    };

    fetchHosts();
  }, [event]);

  const labelText = settings.get('hostedByText', 'Hosted By');

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={cn(styles.modalContent, styles.hostsModal)} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{labelText}</h2>
          <button className={styles.modalClose} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          {loading && (
            <div className={styles.modalLoading}>
              <div className={styles.spinner} />
              <p>Loading hosts...</p>
            </div>
          )}

          {error && (
            <div className={styles.modalError}>
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && hosts.length === 0 && (
            <div className={styles.modalEmpty}>
              <p>No hosts found</p>
            </div>
          )}

          {!loading && !error && hosts.length > 0 && (
            <div className={styles.hostsGrid}>
              {hosts.map((host) => {
                const fullName = `${host.first_name || ''} ${host.last_name || ''}`.trim() || host.email || 'Unknown';
                return (
                  <div key={host.id || host.email} className={styles.hostCard}>
                    <Avatar name={fullName} avatarUrl={host.avatar_url} size={60} removeHostsAvatar={settings.get('removeHostsAvatar')} />
                    <div className={cn(styles.hostName, { [styles.hostNameNoMargin]: settings.get('removeHostsAvatar') })}>{fullName}</div>
                    {host.role && <div className={styles.hostRole}>{host.role}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

HostsModal.propTypes = {
  event: PropTypes.instanceOf(Map).isRequired,
  settings: PropTypes.instanceOf(Map).isRequired,
  onClose: PropTypes.func.isRequired,
};

/**
 * HostedBy component displays event host information
 * Supports visibility and text customization via settings
 */
function HostedBy({ event, settings, className = '' }) {
  const [showModal, setShowModal] = useState(false);
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check if component should be shown
  if (!settings.get('showHostedBy')) {
    return null;
  }

  useEffect(() => {
    const fetchHosts = async () => {
      try {
        setLoading(true);
        const eventId = event.get('id');
        const baseUrl = process.env.EVENTVIEWER_SERVER || '';
        const response = await axios.get(`${baseUrl}/api/v1/event-page/hosts/events/${eventId}`);
        setHosts(response.data.hosts || []);
      } catch (err) {
        console.error('Error fetching hosts:', err);
        setHosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHosts();
  }, [event]);

  // Show loading state
  if (loading) {
    return (
      <div className={cn(styles.eventHostedBy, className)}>
        <div className={styles.hostedByLabel}>
          <i className="fas fa-crown" style={{ marginRight: '8px' }} />
          Loading Host List...
        </div>
      </div>
    );
  }

  // If no hosts found, don't render
  if (!hosts || hosts.length === 0) {
    return null;
  }

  const labelText = settings.get('hostedByText', 'Hosted By');

  // Build the display text for hosts
  const displayNames = hosts.slice(0, 2).map((host) => `${host.first_name || ''} ${host.last_name || ''}`.trim() || host.email || 'Unknown');

  const additionalHostsCount = hosts.length > 2 ? hosts.length - 2 : 0;

  // Get first 3 hosts for avatar display
  const displayHosts = hosts.slice(0, 3);

  return (
    <>
      <div
        className={cn(styles.eventHostedBy, styles.clickable, className)}
        onClick={() => setShowModal(true)}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setShowModal(true);
          }
        }}
      >
        <div className={styles.hostedByInfo}>
          <div className={styles.hostedByLabel}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
              <path
                d="M3.33333 10.667L2 3.33366L5.66667 6.66699L8 2.66699L10.3333 6.66699L14 3.33366L12.6667 10.667H3.33333ZM12.6667 12.667C12.6667 13.067 12.4 13.3337 12 13.3337H4C3.6 13.3337 3.33333 13.067 3.33333 12.667V12.0003H12.6667V12.667Z"
                fill="#DC9C25"
              />
            </svg>
            {labelText}
          </div>
          <div className={styles.hostedByContent}>
            <span className={styles.hostName}>{displayNames.join(', ')}</span>
            {additionalHostsCount > 0 && (
              <span className={styles.additionalHosts}>
                {' '}
                and {additionalHostsCount} more {additionalHostsCount === 1 ? 'host' : 'hosts'}
              </span>
            )}
          </div>
        </div>
        <div className={styles.avatarsContainer}>
          {displayHosts.map((host) => {
            const fullName = `${host.first_name || ''} ${host.last_name || ''}`.trim() || host.email || 'Unknown';
            return (
              <Avatar key={host.id} name={fullName} removeHostsAvatar={settings.get('removeHostsAvatar')} avatarUrl={host.avatar_url} size={36} />
            );
          })}
        </div>
      </div>

      {showModal && <HostsModal event={event} settings={settings} onClose={() => setShowModal(false)} />}
    </>
  );
}

HostedBy.propTypes = {
  event: PropTypes.instanceOf(Map).isRequired,
  settings: PropTypes.instanceOf(Map).isRequired,
  className: PropTypes.string,
};

export default HostedBy;
